import React, { useEffect, useRef, useState } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import _ from 'lodash';

// utils
import chartsUtils, { DataCode, Dataset } from 'utils/charts.utils';

//chart options
import { barChartoptions } from './barChartNewOptions';

// styles
import './barChartNew.scss';
import { ChartComboItem } from '../chartCombo/chartCombo';

interface BarChartNewProps {
  title: string;
  options?: any;
  dataset: Dataset;
  localization: ChartComboItem;
  year: number;
  onNoData: () => void;
  onCallback: (chart: React.MutableRefObject<null>) => void;
}

const BarChartNew = (props: BarChartNewProps) => {
  const chartComponent = useRef(null);
  const [chartOptions, setChartOptions] = useState<any>(_.merge({}, barChartoptions, props.options));

  const getChartSeriesData = () => {
    let result = [];
    let noValues = 0;

    let data = getYearData('vor Steuern', DataCode.vorSteuern, props.localization.value, props.year, '#21689d');
    if (data != null) {
      if (data.y == null) {
        noValues++;
      }
      result.push(data);
    }

    data = getYearData('nach Steuern', DataCode.nachSteuern, props.localization.value, props.year, '#21689d');
    if (data != null) {
      if (data.y == null) {
        noValues++;
      }
      result.push(data);
    }

    data = getYearData('Umverteilung', DataCode.umverteilung, props.localization.value, props.year, '#e20000');
    if (data != null) {
      if (data.y == null) {
        noValues++;
      }
      result.push(data);
    }

    return [
      {
        showInLegend: false,
        data: noValues < 3 ? result : [],
      },
    ];
  };

  const getYearData = (name: string, dataCode: DataCode, localizationCode: string, year: number, color: string) => {
    const localizations = chartsUtils.getLocalizations(props.dataset, dataCode);
    if (!localizations) {
      return null;
    }
    const localization = localizations.filter((l) => l.code === localizationCode);
    if (!localization) {
      return null;
    }
    const yearData = chartsUtils.getYearData(props.dataset, dataCode, year);
    if (!yearData) {
      return null;
    }

    return {
      name,
      color,
      y: yearData[localization[0].index],
      custom: {
        label: dataCode === DataCode.umverteilung ? 'UMVERTEILUNG' : 'ANTEIL',
      },
    };
  };

  const getTitle = () => {
    return `Top ${props.dataset.name} Einkommensanteile und Umverteilung ${props.localization.text} ${props.year}`;
  };

  const loadChart = () => {
    const data = getChartSeriesData();
    if (data[0].data.length == 0) {
      props.onNoData();
      return;
    }

    setChartOptions((prevOptions: any) => ({
      ...prevOptions,
      yAxis: [
        {
          title: {
            text: 'Einkommensanteil (in Prozent)',
            style: {
              color: '#21689d',
            },
          },
          //max: yAxisMaxValue,
          //tickInterval: 10,
        },
        {
          title: {
            text: 'Umverteilung (in Prozentpunkten)',
            style: {
              color: '#e20000',
            },
          },
          opposite: true,
        },
      ],
      tooltip: {
        ...prevOptions.tooltip,
        headerFormat: `<div class="hicharttooltip"><span class="hicharttooltip-title">${props.localization.text}</span>`,
        pointFormatter: function () {
          return `
            <table class="hicharttooltip-table">
              <tr>
                <th>JAHR</th>
                <th style="text-align:right;">${this.custom.label}</th>
              </tr>
              <tr class="hicharttooltip-table-values">
                <td>${props.year}</td>
                <td style="text-align:right;">${Highcharts.numberFormat(this.y, 2)}</td>
              </tr>
            </table>
        `;
        },
      },
      exporting: {
        ...prevOptions.exporting,
        chartOptions: {
          ...prevOptions.exporting.chartOptions,
          title: {
            text: getTitle(),
          },
        },
      },
      series: data,
    }));
  };

  const handleCallback = (chart: Highcharts.Chart) => {
    props.onCallback(chartComponent);
  };

  useEffect(() => {
    loadChart();
  }, [props.dataset, props.year, props.localization]);

  return (
    <div className="barChartNew">
      <div className="chart-title">{getTitle()}</div>
      <div className="barChartNew-chart-wrapper">
        <div className="barChartNew-chart">
          <HighchartsReact ref={chartComponent} highcharts={Highcharts} options={chartOptions} callback={handleCallback} />
        </div>
      </div>
    </div>
  );
};

export default BarChartNew;
